/**
 * MapBoundary's state machine — pure, so it is tested without a DOM (#7696).
 *
 * A boundary is in exactly one of three phases, and the third is the one this
 * module exists for:
 *
 *   * `placeholder` — the request-free stand-in (D13), before the map is wanted.
 *   * `live`        — the Maps JS SDK is mounted (or loading).
 *   * `unavailable` — the SDK could NOT LOAD: the browser never received
 *     `maps.googleapis.com` (mainland China, a blocking network or extension,
 *     a crawler with third-party fetches off). The relay census measured this
 *     on ~13% of estate views and 21% on zh-CN, and until this phase existed
 *     the page showed an EMPTY 380 px box for as long as the reader stayed —
 *     the live map's container, with nothing ever painted into it.
 *
 * `unavailable` is TERMINAL: a script that failed to load does not load on a
 * second mount (the loader memoises its failure), so nothing transitions out
 * of it — not a tap, not scrolling back into view — and the boundary renders
 * its Google-free fallback for the rest of the page's life. A TIMED-OUT load
 * is terminal by the same rule, even though the hung request might still land
 * later: a box that swaps fallback → map under the reader's eyes, after they
 * have read the address, is worse than a fallback that stays.
 *
 * It is a VARIANT, never a boolean beside the others: "live AND failed" is not
 * a state the component can hold.
 *
 * Four transitions enter it. Two are the script not arriving (#8204): it
 * ERRORED (`<APIProvider onError>`), or it neither loaded nor errored within
 * `MAPS_SCRIPT_LOAD_DEADLINE_MS` — a blackholed connection never fires
 * `error`, and before the deadline such a page kept the empty box forever.
 * Two are the script arriving and the map still being unusable (#9393):
 * Google REFUSED the key (vis.gl's `APILoadingStatus.AUTH_FAILURE`, under
 * which vis.gl swaps the map for its own unstyled English error box), or a
 * marker-bearing map reported Advanced Markers UNAVAILABLE (no usable Map ID
 * for this key's project), under which the SDK opens its own modal "Error"
 * alertdialog over the map — whose two `<div tabindex="0" aria-hidden="true">`
 * focus-trap sentinels were the `/listings/rent` `aria-hidden-focus` red.
 * Neither is our UI, and a map that cannot work is not a map, so both unmount
 * it for the same fallback. Terminal by the same rule: a refused key or an
 * unusable Map ID does not recover on a remount.
 */
import type { WebMapsDegradedReason } from '@/shared/lib/metrics/client-metrics_gen';

/**
 * Why a map is unavailable — the subset of the generated `web_maps_degraded`
 * reasons that leaves NO usable map to show: the `script_load_*` family,
 * DERIVED by its prefix from the vocabulary (`web/manifests/metrics.yaml`),
 * never listed by hand, plus the two named post-load failures that leave a map
 * nobody can use (#9393): `auth_failure` (Google refused the key) and
 * `advanced_markers_unavailable` (the SDK's modal "Error" dialog over a
 * marker-bearing map). `dev_watermark` alone still paints a WORKING map (the
 * overlay is a watermark, not a modal), so it is reported, not rendered
 * around. A new `script_load_*` value widens this type, and
 * {@link UNAVAILABLE_CAUSE_OF}'s test then fails to type-check until a
 * transition enters it.
 */
export type MapUnavailableCause = Extract<
  WebMapsDegradedReason,
  `script_load_${string}` | 'auth_failure' | 'advanced_markers_unavailable'
>;

/** No usable map; the boundary renders its Google-free fallback. */
export interface MapUnavailable {
  readonly kind: 'unavailable';
  readonly cause: MapUnavailableCause;
}

/** The boundary's phase. */
export type MapPhase =
  | { readonly kind: 'placeholder' }
  | { readonly kind: 'live' }
  | MapUnavailable;

/** The events that end the SDK load without a map. */
export type ScriptLoadEndedEvent =
  /** `<APIProvider onError>`: the SDK script did not load. */
  | { readonly kind: 'script_load_failed' }
  /** The SDK neither loaded nor errored within `MAPS_SCRIPT_LOAD_DEADLINE_MS`. */
  | { readonly kind: 'script_load_timed_out' };

/** The SDK loaded and the map is still unusable (#9393). */
export type MapUnusableEvent =
  /** vis.gl's `APILoadingStatus.AUTH_FAILURE`: Google refused the key. */
  | { readonly kind: 'auth_failed' }
  /** A marker-bearing map reported `isAdvancedMarkersAvailable === false`. */
  | { readonly kind: 'advanced_markers_unavailable' };

/** The events that leave a boundary with no usable map. */
export type MapUnavailableEvent = ScriptLoadEndedEvent | MapUnusableEvent;

/** What can happen to a boundary. */
export type MapPhaseEvent =
  /** The reader asked for the map (`load="tap"`'s button). */
  { readonly kind: 'requested' } | MapUnavailableEvent;

/**
 * Which cause each unavailable event records — one row per event, and (held
 * by `map-phase.test.ts`'s type assertion) one event per cause.
 */
export const UNAVAILABLE_CAUSE_OF = {
  script_load_failed: 'script_load_error',
  script_load_timed_out: 'script_load_timeout',
  auth_failed: 'auth_failure',
  advanced_markers_unavailable: 'advanced_markers_unavailable',
} as const satisfies Record<MapUnavailableEvent['kind'], MapUnavailableCause>;

export const INITIAL_MAP_PHASE: MapPhase = { kind: 'placeholder' };

/** The transition function (a `useReducer` reducer). Total and pure. */
export function nextMapPhase(phase: MapPhase, event: MapPhaseEvent): MapPhase {
  if (phase.kind === 'unavailable') return phase;
  if (event.kind === 'requested') return phase.kind === 'live' ? phase : { kind: 'live' };
  return { kind: 'unavailable', cause: UNAVAILABLE_CAUSE_OF[event.kind] };
}

/** What the boundary renders. */
export type MapView = MapPhase['kind'];

/**
 * Resolve the RENDERED view from the held phase and the two inputs that are
 * not state: an `in-view` boundary's "seen" (a hook value), and the caller's
 * `defer`. `unavailable` wins over both — a failed SDK is not waiting on
 * anything — and `defer` holds the placeholder otherwise.
 */
export function resolveMapView(
  phase: MapPhase,
  inputs: { readonly seenInView: boolean; readonly defer: boolean }
): MapView {
  if (phase.kind === 'unavailable') return 'unavailable';
  if (inputs.defer) return 'placeholder';
  return phase.kind === 'live' || inputs.seenInView ? 'live' : 'placeholder';
}

/**
 * The "open in maps" link for a coordinate. A LINK, not a load: the failure is
 * that the browser cannot fetch Google's SCRIPT (or Google refused this page's
 * key), and a link costs the page nothing and carries no key — the reader's own
 * maps app or browser decides whether it can reach it. Google's documented, keyless Maps URL (`api=1`), which every platform's
 * maps handler accepts.
 */
export function openInMapsHref(pin: { readonly lat: number; readonly lng: number }): string {
  return `https://www.google.com/maps/search/?api=1&query=${pin.lat},${pin.lng}`;
}
